import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Filter, Calendar, CreditCard, Download, Trash, AlertTriangle, Eye, Mail, Archive, ArrowDownUp } from 'lucide-react';
import { useDocuments, Document } from '../context/DocumentsContext';
import { useClients } from '../context/ClientsContext';
import { useAuth } from '../context/AuthContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';
import Select from '../components/UI/Select';
import JSZip from 'jszip';
import EmailDocumentModal from '../components/UI/EmailDocumentModal';

type FilterOptions = {
  type: Document['type'] | 'all';
  status: Document['status'] | 'all';
  searchTerm: string;
};

const DocumentHistory: React.FC = () => {
  const { documents, deleteDocument, updateDocument, addDocument } = useDocuments();
  const { clients } = useClients();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [convertingDocumentId, setConvertingDocumentId] = useState<string | null>(null);
  
  const [filters, setFilters] = useState<FilterOptions>({
    type: 'all',
    status: 'all',
    searchTerm: '',
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const getStatusStyles = (status: Document['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
      case 'sent':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300';
      case 'paid':
        return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const getStatusText = (status: Document['status']) => {
    switch (status) {
      case 'draft':
        return 'Brouillon';
      case 'sent':
        return 'Envoyé';
      case 'paid':
        return 'Payé';
      case 'cancelled':
        return 'Annulé';
      default:
        return status;
    }
  };

  const handleConvertToInvoice = async (document: Document) => {
    setConvertingDocumentId(document.id);
    try {
      const newInvoice = await addDocument({
        type: 'invoice',
        client_id: document.client_id,
        date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        prompt: document.prompt,
        amount: document.amount,
        vat: document.vat,
        vat_rate: document.vat_rate,
        status: 'draft',
        line_items: document.line_items,
      });

      await updateDocument(document.id, { status: 'paid' });
      navigate(`/app/documents/${newInvoice.id}`);
    } catch (error) {
      console.error('Error converting quote to invoice:', error);
    } finally {
      setConvertingDocumentId(null);
    }
  };
  
  const filteredDocuments = documents.filter((doc) => {
    const typeMatch = filters.type === 'all' || doc.type === filters.type;
    const statusMatch = filters.status === 'all' || doc.status === filters.status;
    const searchMatch = filters.searchTerm === '' || 
      doc.number.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      (clients.find(c => c.id === doc.client_id)?.name || '').toLowerCase().includes(filters.searchTerm.toLowerCase());
    
    return typeMatch && statusMatch && searchMatch;
  });
  
  const handleFilterChange = (name: keyof FilterOptions, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const resetFilters = () => {
    setFilters({
      type: 'all',
      status: 'all',
      searchTerm: '',
    });
  };
  
  const confirmDelete = (id: string) => {
    setDeleteId(id);
  };
  
  const handleDelete = () => {
    if (deleteId) {
      deleteDocument(deleteId);
      setDeleteId(null);
    }
  };

  const handleStatusChange = async (documentId: string, newStatus: Document['status']) => {
    try {
      await updateDocument(documentId, { status: newStatus });
    } catch (error) {
      console.error('Error updating document status:', error);
    }
  };

  const handleEmail = (doc: Document) => {
    setSelectedDocument(doc);
    setShowEmailModal(true);
  };

  const handleDownloadAll = async () => {
    try {
      setIsDownloading(true);
      const zip = new JSZip();

      const invoicesFolder = zip.folder("factures");
      const quotesFolder = zip.folder("devis");

      // Process documents sequentially to avoid overwhelming the server
      for (const doc of documents) {
        const client = clients.find(c => c.id === doc.client_id);
        if (!client) continue;

        const folder = doc.type === 'invoice' ? invoicesFolder : quotesFolder;
        const fileName = `${doc.type === 'invoice' ? 'Facture' : 'Devis'}_${doc.number}.pdf`;
        
        try {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf?id=${doc.id}`,
            {
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              },
            }
          );

          if (!response.ok) {
            console.error(`Failed to generate PDF for document ${doc.number}`);
            continue;
          }

          const pdfBlob = await response.blob();
          folder?.file(fileName, pdfBlob);
        } catch (error) {
          console.error(`Error generating PDF for document ${doc.number}:`, error);
          continue;
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = "documents.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading documents:', error);
    } finally {
      setIsDownloading(false);
    }
  };
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Documents</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gérez vos factures et devis
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline"
            leftIcon={<Archive className="h-4 w-4" />}
            onClick={handleDownloadAll}
            isLoading={isDownloading}
          >
            Télécharger tout
          </Button>
          <Button 
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/app/create')}
          >
            Créer un Document
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
          <Input
            placeholder="Rechercher des documents..."
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Button 
          variant="outline"
          leftIcon={<Filter className="h-4 w-4" />}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filtrer
        </Button>
      </div>
      
      {showFilters && (
        <Card className="animate-slide-down">
          <Card.Content>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Select
                label="Type de Document"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                options={[
                  { value: 'all', label: 'Tous les Types' },
                  { value: 'invoice', label: 'Factures uniquement' },
                  { value: 'quote', label: 'Devis uniquement' },
                ]}
              />
              
              <Select
                label="Statut"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                options={[
                  { value: 'all', label: 'Tous les Statuts' },
                  { value: 'draft', label: 'Brouillon' },
                  { value: 'sent', label: 'Envoyé' },
                  { value: 'paid', label: 'Payé' },
                  { value: 'cancelled', label: 'Annulé' },
                ]}
              />
              
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={resetFilters}
                  className="mb-1"
                >
                  Réinitialiser les Filtres
                </Button>
              </div>
            </div>
          </Card.Content>
        </Card>
      )}
      
      {deleteId && (
        <Card className="border-error-300 dark:border-error-700 animate-slide-up">
          <Card.Content className="space-y-4">
            <div className="flex items-start">
              <div className="mr-4 p-2 rounded-full bg-error-100 dark:bg-error-900/20 text-error-500">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Supprimer le Document</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.
                </p>
              </div>
            </div>
          </Card.Content>
          
          <Card.Footer className="flex justify-end space-x-3">
            <Button 
              variant="outline" 
              onClick={() => setDeleteId(null)}
            >
              Annuler
            </Button>
            <Button 
              variant="outline"
              className="text-error-500 border-error-300 hover:bg-error-50 dark:border-error-700 dark:hover:bg-error-900/20"
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </Card.Footer>
        </Card>
      )}
      
      <div className="space-y-4">
        {filteredDocuments.length > 0 ? (
          filteredDocuments
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((doc, index) => {
              const client = clients.find(c => c.id === doc.client_id);
              
              return (
                <Card 
                  key={doc.id} 
                  className="animate-slide-up cursor-pointer hover:shadow-lg transition-shadow"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => navigate(`/app/documents/${doc.id}`)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between p-2">
                    <div className="flex items-center">
                      <div className={`
                        p-3 rounded-md mr-4
                        ${doc.type === 'invoice' 
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                          : 'bg-secondary-50 dark:bg-secondary-900/20 text-secondary-600 dark:text-secondary-400'
                        }
                      `}>
                        <FileText className="h-6 w-6" />
                      </div>
                      
                      <div>
                        <h3 className="font-medium text-lg">
                          {doc.type === 'invoice' ? 'Facture' : 'Devis'} #{doc.number}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {client ? client.name : 'Client Inconnu'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center mt-4 md:mt-0">
                      <div className="flex flex-col md:flex-row md:items-center md:space-x-8 text-sm">
                        <div className="flex items-center mb-2 md:mb-0">
                          <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                          <span>{new Date(doc.date).toLocaleDateString()}</span>
                        </div>
                        
                        <div className="flex items-center mb-2 md:mb-0">
                          <CreditCard className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                          <span className="whitespace-nowrap">{doc.amount.toLocaleString()} {user?.currency_symbol}</span>
                        </div>
                        
                        <Select
                          value={doc.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateDocument(doc.id, { status: e.target.value as Document['status'] });
                          }}
                          options={[
                            { value: 'draft', label: 'Brouillon' },
                            { value: 'sent', label: 'Envoyé' },
                            { value: 'paid', label: 'Payé' },
                            { value: 'cancelled', label: 'Annulé' },
                          ]}
                          className={`w-32 ${getStatusStyles(doc.status)}`}
                        />
                      </div>
                      
                      <div className="flex ml-6 space-x-2">
                        {doc.type === 'quote' && doc.status !== 'paid' && (
                          <Button 
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConvertToInvoice(doc);
                            }}
                            isLoading={convertingDocumentId === doc.id}
                            title="Transformer en facture"
                          >
                            <ArrowDownUp className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDocument(doc);
                            setShowEmailModal(true);
                          }}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm"
                          variant="ghost"
                          className="text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(doc.id);
                          }}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
        ) : (
          <div className="py-12 flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 mb-4">
              <FileText className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Aucun document trouvé</h3>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              {filters.searchTerm || filters.type !== 'all' || filters.status !== 'all'
                ? "Aucun document ne correspond à vos filtres actuels."
                : "Vous n'avez pas encore créé de documents."}
            </p>
            {filters.searchTerm || filters.type !== 'all' || filters.status !== 'all' ? (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={resetFilters}
              >
                Effacer les Filtres
              </Button>
            ) : (
              <Button 
                variant="outline" 
                className="mt-4"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => navigate('/app/create')}
              >
                Créer Votre Premier Document
              </Button>
            )}
          </div>
        )}
      </div>

      {selectedDocument && (
        <EmailDocumentModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedDocument(null);
          }}
          documentNumber={selectedDocument.number}
          documentType={selectedDocument.type}
          clientEmail={clients.find(c => c.id === selectedDocument.client_id)?.email || ''}
          clientName={clients.find(c => c.id === selectedDocument.client_id)?.name || ''}
          onSuccess={() => {
            handleStatusChange(selectedDocument.id, 'sent');
            setShowEmailModal(false);
            setSelectedDocument(null);
          }}
          documentId={selectedDocument.id}
        />
      )}
    </div>
  );
};

export default DocumentHistory;